import { ReactNode } from 'react';
import {
  MdArrowForward,
  MdBlock,
  MdChatBubbleOutline,
  MdCheckCircle,
  MdErrorOutline,
  MdSchedule
} from 'react-icons/md';
import {
  MEDIA_INPUTS_SECTIONS,
  SectionKey,
  SectionKeyType,
  SectionStatus,
  SectionStatusType
} from '../data-qa-constants';
import { CampaignPillType } from '../data-qa-utils';
import { combineSectionStatus } from '../media-inputs-utils';

// Steps mirror our Streamlit prototype's stepper (Campaigns sits before
// Portfolio). About and Summary are navigable but carry no approval status; the
// seven review steps map to a SectionKey so their status badge is derived.
// Feature Mapping moved before Portfolio/Channels (Sean, 2026-08-27) -- it's
// transparency-only, so sandwiching it between Portfolio/Channels (which need
// review) and Context Variables/Dependent Variable (which also need review) read
// as odd; grouping all four review sections together instead.
export const StepKey = {
  ABOUT: 'about',
  CAMPAIGNS: 'campaigns',
  // Portfolio and Channels, reviewed together.
  MEDIA_INPUTS: 'mediaInputs',
  FEATURE_MAPPING: 'featureMapping',
  CONTEXT_VARS: 'contextVars',
  DEPENDENT_VAR: 'dependentVar',
  SUMMARY: 'summary'
} as const;
export type StepKeyType = (typeof StepKey)[keyof typeof StepKey];

interface StepDef {
  key: StepKeyType;
  label: string;
  // The persisted section(s) this step reviews -- Media Inputs covers two, decided
  // together.
  sections?: SectionKeyType[];
}

export const STEPPER_STEPS: StepDef[] = [
  { key: StepKey.ABOUT, label: 'About' },
  { key: StepKey.CAMPAIGNS, label: 'Campaigns', sections: [SectionKey.CAMPAIGNS] },
  // No `section` -- Feature Mapping dropped approve/flag entirely (Sean, 2026-08-27,
  // transparency-only per its own on-page copy), so there's no review status to badge.
  { key: StepKey.FEATURE_MAPPING, label: 'Feature Mapping' },
  { key: StepKey.MEDIA_INPUTS, label: 'Media Inputs', sections: MEDIA_INPUTS_SECTIONS },
  {
    key: StepKey.CONTEXT_VARS,
    label: 'Context Variables',
    sections: [SectionKey.CONTEXT_VARS]
  },
  {
    key: StepKey.DEPENDENT_VAR,
    label: 'Dependent Variable',
    sections: [SectionKey.DEPENDENT_VAR]
  },
  { key: StepKey.SUMMARY, label: 'Summary' }
];

interface Badge {
  variant: 'done' | 'review' | 'flagged';
  label: string;
  icon: ReactNode;
}

const badgeForStatus = (status: SectionStatusType): Badge => {
  if (status === SectionStatus.APPROVED) {
    return { variant: 'done', label: 'Done', icon: <MdCheckCircle /> };
  }
  if (status === SectionStatus.FLAGGED) {
    return { variant: 'flagged', label: 'Flagged', icon: <MdErrorOutline /> };
  }
  return { variant: 'review', label: 'Needs Review', icon: <MdArrowForward /> };
};

// Campaigns is a labeling gate, not an approve/flag section, so it carries its
// own pill derived from the labeling state (see campaignPillStatus).
const campaignBadge = (status: CampaignPillType): Badge => {
  if (status === 'blocking') {
    return { variant: 'flagged', label: 'Blocking', icon: <MdBlock /> };
  }
  if (status === 'labelSoon') {
    return { variant: 'review', label: 'Label soon', icon: <MdSchedule /> };
  }
  return { variant: 'done', label: 'Labeled', icon: <MdCheckCircle /> };
};

interface SectionStepperProps {
  activeStep: StepKeyType;
  sectionStatus: Record<SectionKeyType, SectionStatusType>;
  onSelect: (step: StepKeyType) => void;
  // Campaigns pill state, derived from the taxonomy-labeling result. When set, it
  // overrides the generic status badge for the Campaigns step.
  campaignStatus?: CampaignPillType;
  // Sections blocked by a data problem (e.g. negative values) -- their badge reads
  // "Blocking" whatever their review status, since they can't be approved as-is.
  blockedSections?: ReadonlySet<SectionKeyType>;
  // Comments in each tab's open thread (see CommentsDock), shown beside its label.
  commentCounts?: Partial<Record<string, number>>;
}

// Sticky section stepper (matches the Streamlit prototype): one row of steps,
// each with a status badge above its label; the active step is underlined.
// Navigates one section at a time.
export const SectionStepper = ({
  activeStep,
  sectionStatus,
  onSelect,
  campaignStatus,
  blockedSections,
  commentCounts
}: SectionStepperProps) => (
  <nav className='data-qa-stepper'>
    {STEPPER_STEPS.map(step => {
      const isActive = step.key === activeStep;
      let badge: Badge | undefined;
      if (step.key === StepKey.CAMPAIGNS && campaignStatus) {
        badge = campaignBadge(campaignStatus);
      } else if (step.sections?.some(section => blockedSections?.has(section))) {
        badge = { variant: 'flagged', label: 'Blocking', icon: <MdBlock /> };
      } else if (step.sections) {
        badge = badgeForStatus(
          combineSectionStatus(step.sections.map(section => sectionStatus[section]))
        );
      }
      return (
        <button
          key={step.key}
          type='button'
          className={`data-qa-stepper__step${isActive ? ' data-qa-stepper__step--active' : ''}`}
          aria-current={isActive ? 'step' : undefined}
          onClick={() => onSelect(step.key)}
        >
          {badge ? (
            <span className={`data-qa-stepper__badge data-qa-stepper__badge--${badge.variant}`}>
              {badge.icon}
              {badge.label}
            </span>
          ) : (
            <span className='data-qa-stepper__badge data-qa-stepper__badge--none' />
          )}
          <span className='data-qa-stepper__label'>
            {step.label}
            {Boolean(commentCounts?.[step.key]) && (
              <span className='data-qa-stepper__comments'>
                <MdChatBubbleOutline aria-hidden />
                {commentCounts?.[step.key]}
                <span className='visually-hidden'> comments</span>
              </span>
            )}
          </span>
        </button>
      );
    })}
  </nav>
);
